// src/services/csv.service.ts
import Papa from 'papaparse';


export interface CsvParseResult {
  data: Record<string, string>[];
  headers: string[];
  rowCount: number;
  errors: Papa.ParseError[];
}

export interface ColumnMapping {
  street: string | null;
  unit: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  // Optional additional fields
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  email: string | null;
  notes: string | null;
  // Custom tag columns
  tags: string[];
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
  validRows: number;
  invalidRows: number;
  data: ParsedAddressRow[];
}

export interface ValidationError {
  row: number;
  column: string;
  value: string;
  message: string;
}

export interface ValidationWarning {
  row: number;
  column: string;
  value: string;
  message: string;
}

export interface ParsedAddressRow {
  rowIndex: number;
  street: string;
  unit?: string;
  city: string;
  state: string;
  zipCode: string;
  fullAddress: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  notes?: string;
  tags: Record<string, string>;
  isValid: boolean;
  errors: string[];
}


export function parseCSV(
  file: File,
  options?: {
    preview?: number;
    onProgress?: (progress: number) => void;
  }
): Promise<CsvParseResult> {
  return new Promise((resolve, reject) => {
    const result: CsvParseResult = {
      data: [],
      headers: [],
      rowCount: 0,
      errors: [],
    };

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      preview: options?.preview,
      complete: (parseResult) => {
        result.data = parseResult.data as Record<string, string>[];
        result.headers = parseResult.meta.fields || [];
        result.rowCount = result.data.length;
        result.errors = parseResult.errors;
        resolve(result);
      },
      error: (error) => {
        reject(new Error(`CSV parsing failed: ${error.message}`));
      },
    });
  });
}

export function parseCSVString(csvString: string): CsvParseResult {
  const parseResult = Papa.parse(csvString, {
    header: true,
    skipEmptyLines: true,
  });

  return {
    data: parseResult.data as Record<string, string>[],
    headers: parseResult.meta.fields || [],
    rowCount: parseResult.data.length,
    errors: parseResult.errors,
  };
}


const COLUMN_PATTERNS: Record<keyof Omit<ColumnMapping, 'tags'>, RegExp[]> = {
  street: [/street|address|addr|address.?1|street.?address/i],
  unit: [/unit|apt|apartment|suite|ste|address.?2/i],
  city: [/city|town|municipality/i],
  state: [/state|st|province|region/i],
  zipCode: [/zip|postal|zip.?code|postal.?code/i],
  firstName: [/first.?name|fname|given.?name/i],
  lastName: [/last.?name|lname|surname|family.?name/i],
  phone: [/phone|tel|telephone|mobile|cell/i],
  email: [/email|e-?mail/i],
  notes: [/notes?|comments?|remarks?/i],
};

export function detectColumnMapping(headers: string[]): ColumnMapping {
  const mapping: ColumnMapping = {
    street: null,
    unit: null,
    city: null,
    state: null,
    zipCode: null,
    firstName: null,
    lastName: null,
    phone: null,
    email: null,
    notes: null,
    tags: [],
  };

  const usedHeaders = new Set<string>();

  // Match each field to the best header
  for (const [field, patterns] of Object.entries(COLUMN_PATTERNS)) {
    for (const header of headers) {
      if (usedHeaders.has(header)) continue;

      for (const pattern of patterns) {
        if (pattern.test(header)) {
          (mapping as any)[field] = header;
          usedHeaders.add(header);
          break;
        }
      }
    }
  }

  // Remaining headers become potential tag columns
  mapping.tags = headers.filter((h) => !usedHeaders.has(h));

  return mapping;
}

export function validateColumnMapping(mapping: ColumnMapping): { valid: boolean; missing: string[] } {
  const required: (keyof ColumnMapping)[] = ['street', 'city', 'state', 'zipCode'];
  const missing = required.filter((field) => !mapping[field]);

  return {
    valid: missing.length === 0,
    missing,
  };
}


export function validateData(
  data: Record<string, string>[],
  mapping: ColumnMapping
): ValidationResult {
  const result: ValidationResult = {
    valid: true,
    errors: [],
    warnings: [],
    validRows: 0,
    invalidRows: 0,
    data: [],
  };

  data.forEach((row, index) => {
    const rowIndex = index + 2; // +2 for 1-indexing and header row
    const rowErrors: string[] = [];
    const parsedRow: ParsedAddressRow = {
      rowIndex,
      street: '',
      city: '',
      state: '',
      zipCode: '',
      fullAddress: '',
      tags: {},
      isValid: true,
      errors: [],
    };

    // Extract and validate required fields
    if (mapping.street) {
      parsedRow.street = cleanString(row[mapping.street]);
      if (!parsedRow.street) {
        rowErrors.push('Missing street address');
        result.errors.push({
          row: rowIndex,
          column: mapping.street,
          value: row[mapping.street] || '',
          message: 'Street address is required',
        });
      }
    }

    if (mapping.city) {
      parsedRow.city = cleanString(row[mapping.city]);
      if (!parsedRow.city) {
        rowErrors.push('Missing city');
        result.errors.push({
          row: rowIndex,
          column: mapping.city,
          value: row[mapping.city] || '',
          message: 'City is required',
        });
      }
    }

    if (mapping.state) {
      parsedRow.state = normalizeState(row[mapping.state]);
      if (!parsedRow.state) {
        rowErrors.push('Missing or invalid state');
        result.errors.push({
          row: rowIndex,
          column: mapping.state,
          value: row[mapping.state] || '',
          message: 'State is required (use 2-letter abbreviation)',
        });
      }
    }

    if (mapping.zipCode) {
      parsedRow.zipCode = normalizeZipCode(row[mapping.zipCode]);
      if (!parsedRow.zipCode) {
        rowErrors.push('Missing or invalid ZIP code');
        result.errors.push({
          row: rowIndex,
          column: mapping.zipCode,
          value: row[mapping.zipCode] || '',
          message: 'Valid ZIP code is required',
        });
      }
    }

    // Extract optional fields
    if (mapping.unit) {
      parsedRow.unit = cleanString(row[mapping.unit]) || undefined;
    }

    if (mapping.firstName) {
      parsedRow.firstName = cleanString(row[mapping.firstName]) || undefined;
    }

    if (mapping.lastName) {
      parsedRow.lastName = cleanString(row[mapping.lastName]) || undefined;
    }

    if (mapping.phone) {
      const phone = row[mapping.phone];
      if (phone) {
        const normalizedPhone = normalizePhone(phone);
        if (normalizedPhone) {
          parsedRow.phone = normalizedPhone;
        } else {
          result.warnings.push({
            row: rowIndex,
            column: mapping.phone,
            value: phone,
            message: 'Invalid phone number format',
          });
        }
      }
    }

    if (mapping.email) {
      const email = row[mapping.email];
      if (email) {
        if (isValidEmail(email)) {
          parsedRow.email = email.toLowerCase().trim();
        } else {
          result.warnings.push({
            row: rowIndex,
            column: mapping.email,
            value: email,
            message: 'Invalid email format',
          });
        }
      }
    }

    if (mapping.notes) {
      parsedRow.notes = cleanString(row[mapping.notes]) || undefined;
    }

    // Extract tag columns
    mapping.tags.forEach((tagColumn) => {
      const value = cleanString(row[tagColumn]);
      if (value) {
        parsedRow.tags[tagColumn] = value;
      }
    });

    // Build full address
    parsedRow.fullAddress = buildFullAddress(parsedRow);

    // Set validity
    parsedRow.isValid = rowErrors.length === 0;
    parsedRow.errors = rowErrors;

    if (parsedRow.isValid) {
      result.validRows++;
    } else {
      result.invalidRows++;
      result.valid = false;
    }

    result.data.push(parsedRow);
  });

  return result;
}


function cleanString(value: string | undefined | null): string {
  if (!value) return '';
  return value.toString().trim();
}

function normalizeState(value: string | undefined | null): string {
  if (!value) return '';

  const cleaned = cleanString(value).toUpperCase();

  // If already 2 letters, validate it's a US state
  if (cleaned.length === 2 && US_STATES.includes(cleaned)) {
    return cleaned;
  }

  // Try to match full state name
  const stateEntry = STATE_NAMES[cleaned.toLowerCase()];
  if (stateEntry) {
    return stateEntry;
  }

  return '';
}

function normalizeZipCode(value: string | undefined | null): string {
  if (!value) return '';

  // Remove non-digits except hyphen
  const cleaned = cleanString(value).replace(/[^\d-]/g, '');

  // Match 5-digit or 5+4 format
  const match = cleaned.match(/^(\d{5})(-?\d{4})?$/);
  if (match) {
    return match[1]; // Return just the 5-digit part for consistency
  }

  return '';
}

function normalizePhone(value: string): string | null {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');

  // Must have 10 or 11 digits (with or without country code)
  if (digits.length === 10) {
    return digits;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return digits.slice(1);
  }

  return null;
}

function isValidEmail(value: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(value.trim());
}

function buildFullAddress(row: ParsedAddressRow): string {
  const parts = [row.street];
  if (row.unit) parts.push(`Unit ${row.unit}`);
  parts.push(`${row.city}, ${row.state} ${row.zipCode}`);
  return parts.join(', ');
}

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC', 'PR', 'VI', 'GU', 'AS', 'MP',
];

const STATE_NAMES: Record<string, string> = {
  'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR',
  'california': 'CA', 'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE',
  'florida': 'FL', 'georgia': 'GA', 'hawaii': 'HI', 'idaho': 'ID',
  'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA', 'kansas': 'KS',
  'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
  'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS',
  'missouri': 'MO', 'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV',
  'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
  'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH', 'oklahoma': 'OK',
  'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT',
  'vermont': 'VT', 'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV',
  'wisconsin': 'WI', 'wyoming': 'WY', 'district of columbia': 'DC',
  'puerto rico': 'PR', 'virgin islands': 'VI', 'guam': 'GU',
};


export function generateCSV(
  data: Record<string, any>[],
  columns: string[]
): string {
  const rows = data.map((row) =>
    columns.map((col) => {
      const value = row[col];
      if (value === null || value === undefined) return '';
      const stringValue = String(value);
      // Escape quotes and wrap in quotes if contains comma, quote, or newline
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    }).join(',')
  );

  return [columns.join(','), ...rows].join('\n');
}

// Export service object
export const csvService = {
  parse: parseCSV,
  parseString: parseCSVString,
  detectColumnMapping,
  validateColumnMapping,
  validateData,
  generateCSV,
};

export default csvService;
